import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { FilterChangedEvent, GridApi } from "ag-grid-community";
import "ag-grid-enterprise";
import { useTheme } from "@mui/material/styles";
import _ from "lodash";
import axios from "axios";
import moment from "moment";
import { AgGridReact } from "ag-grid-react";
import { clientMostLocatedColDefs, locateTabMostLocatedColDefs, mostLocatedColDefs } from "../../common/locateColumnDefs";
import { constants, EndPoint } from "../../common/constants";
import { serviceAPIMapping } from "../../config/service-api-mapping";
import { convertToCommas, getRowStyle, mapRowId } from "../../common/commonFunctions";
import SpinnerFullScreen from "../SpinnerFullScreen";

interface AgGridProps {
  title?: string;
  onClickHan?: any;
  onClickSelectData?: any;
  onCellChange?: any;
  seTtotalSelectedRows?: any;
  getBtnRef?: any;
  getGridApi?: any;
  doesExternalFilterPass?: any;
  isExternalFilterPresent?: any;
  isDeselectAll?: boolean;
  getContextMenuItems?: any;
  globalSearch?: any;
  isSingleRowInserted?: boolean;
  defaultFilter?: any;
  isMostLocatesRefreshed?: boolean;
  date: any;
  isLocateTab:boolean
  onRowSelect?:any
  isAdmin: boolean
  inputTicker?:string
  isResetFilter?:boolean
globalClient?: any
isShowFilledLocates?:boolean
}

const MostLocatesGrid = ({
  title,
  getGridApi,
  doesExternalFilterPass,
  isExternalFilterPresent,
  isDeselectAll,
  getContextMenuItems,
  globalSearch,
  defaultFilter,
  getBtnRef,
  isMostLocatesRefreshed,
  date,
  isLocateTab,
  onRowSelect,
  isAdmin,
  inputTicker,
  isResetFilter,
  globalClient,
  isShowFilledLocates
}: AgGridProps) => {
  const gridRef = useRef<any>(null);
  const theme = useTheme();
  const [locateRows, setLocateRows] = useState<any>([]);
  const [columnDefs] = useState<any>(isLocateTab?[...locateTabMostLocatedColDefs]:isAdmin && !isLocateTab ?[ ...mostLocatedColDefs]: [...clientMostLocatedColDefs]);
  const [showScreenLoader, setShowScreenLoader] = useState<boolean>(false);
  const [initialLocates, setInitialLocates] = useState<any>([]);

  const [gridTheme, setGridTheme] = useState<string>(
    theme.palette.mode === "dark" ? " ag-theme-balham-dark" : " ag-theme-balham"
  );
  useEffect(() => {
    setGridTheme(
      theme.palette.mode === "dark"
        ? " ag-theme-balham-dark"
        : " ag-theme-balham"
    );
  }, [theme.palette.mode]);

  useEffect(() => {
    if (date) {
     getMostLocates();
    }
  }, [isMostLocatesRefreshed, date, globalClient]);

  let gridApi: any;
  const defaultColDef = useMemo<any>(() => {
    return {
      flex: 1,
      sortable: true,
      filter: true,
      floatingFilter: false,
      suppressFilterButton: false,
      editable: false,
      resizable: true,
      headerCheckboxSelectionFilteredOnly: true,
      menuTabs: [constants.FilterMenuTab],
      tooltipShowDelay: 10,
      tooltipMouseTrack: true,
      suppressKeyboardEvent: (params: any) => {
        const isTabKey = params?.event?.key === constants.Tab;
        if (isTabKey) gridRef?.current?.api?.stopEditing();

        return false;
      },
    };
  }, []);

  useEffect(() => {
    if (defaultFilter && gridRef) {
      const filterComponent = gridRef?.current?.api?.getFilterInstance(
        defaultFilter.column
      );
      if (filterComponent) {
        filterComponent?.setModel({ values: defaultFilter.values });
        gridApi.onFilterChanged();
      }
    }
  }, [gridRef]);

  const getMostLocates = async () => {
    try {
      setShowScreenLoader(isLocateTab?false: true);
      setLocateRows([]);
      const accountId = window.localStorage.getItem("activeAccountCode");
      const clientCode = isAdmin ? (globalClient === 'all' ||  globalClient === ''? null : globalClient) :window.localStorage.getItem("activeClientShortName")
      const request = {
        date: moment(date).format("YYYY-MM-DD"),
        account: accountId,
       clientShortCode: clientCode
      };

      const token = window.localStorage.getItem("refreshtoken");

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      await axios
        .post(
          serviceAPIMapping["LOCATEMANAGEMENT-SERVICE"].apiRouteURL +
            EndPoint.getMostLocate,
          JSON.stringify(request),
          { headers: headers }
        )
        .then(async (response: any) => {
          if (response?.status === 200) {
            const rows = await response?.data?.map((row: any) => {
              return {
                ...row,
                minrate: parseFloat(row?.minrate)?.toFixed(3),
                maxrate: parseFloat(row?.maxrate)?.toFixed(3),
                total_fill_count:row?.total_fill_count || 0
              };
            });
            if (rows && _.isArray(rows) && rows?.length > 0) {
              const sortedRows = _.sortBy(rows, [
                "total_request_count",
              ]).reverse();
              const mappedRows = mapRowId(sortedRows);
              mappedRows.map(
                (r: any) =>
                  (r.avgrate = r.avgrate ? Number(r.avgrate).toFixed(3) : 0)
              );
              if(isShowFilledLocates){
                const filteredRows = [...mappedRows]?.filter((r: any)=> r?.totalFillQty >0)
                setLocateRows(filteredRows);
              }
              else{
                setLocateRows(mappedRows);
              }
              
              setInitialLocates([...mappedRows])
            } else {
              setLocateRows([]);
            }
          } else if (response?.status === 401) {
            //logout(channel)
          } else {
            setLocateRows([]);
          }
        });
    } catch (err) {
      setLocateRows([]);
    } finally {
      setShowScreenLoader(false);
    }
  };

  const onGridReady = (params: any) => {
    params.api.sizeColumnsToFit();
    gridApi = params.api;

    const gridColumnApi = params.columnApi;
    getName(gridApi, gridColumnApi);

    // Emmits the gridApi refrence to the parent component
    if (getGridApi) {
      getGridApi(gridApi, gridColumnApi);
    }
    getBtnRef(gridRef);
  };

  const getMaxValue = (attribute: string, api: GridApi): number => {
    const uniqueItemsSet: any[] = [];
    api?.forEachNodeAfterFilter((rowNode: any) => {
      if (
        rowNode?.data instanceof Object &&
        (_.isString(rowNode.data[attribute])
          ? rowNode.data[attribute] && !_.isEmpty(rowNode.data[attribute])
          : rowNode.data[attribute])
      ) {
        uniqueItemsSet.push(+rowNode.data[attribute]);
      }
    });

    return uniqueItemsSet?.length > 0 ? _.max(uniqueItemsSet) : 0;
  };

  const getName = (api: any, columnApi: any) => {
    if (!api || !columnApi) return;
    const rows: any = {};
    const result = [];
    columnApi?.getAllGridColumns().forEach((item: any) => {
      rows[item.colId] = null;
    });
    const columnsWithAggregation = [
      "total_request_count",
      "total_quantity",
      "total_response_quantity",
      "totalFillQty",
      "minrate",
      "maxrate",
      "avgrate",
      'pnl',
      "total_fill_count"
    ];
    columnsWithAggregation.forEach((element) => {
      api?.forEachNodeAfterFilter((rowNode: any) => {
        if (rowNode.data[element]) {
          rows[element] += Number(parseFloat(rowNode.data[element]));
        }
        
      });
    });

    rows['total_response_quantity'] = _.isNull(rows['total_response_quantity'])?0:rows['total_response_quantity']
    rows['totalFillQty'] = _.isNull(rows['totalFillQty'])?0:rows['totalFillQty']
    rows['total_quantity'] = _.isNull(rows['total_quantity'])?0:rows['total_quantity']
    rows['total_fill_count'] = _.isNull(rows['total_fill_count'])?0:rows['total_fill_count']

    const columnsWithAvg = [
      "minrate",
      "maxrate",
      "avgrate"
    ];
   
    columnsWithAvg.forEach((element) => {
      const nodeCount = api.getDisplayedRowCount();
      const avg = (rows[element] / nodeCount).toString();
      if (nodeCount !== 0 && !_.isNaN(avg)) {
        rows[element] = Number(parseFloat(avg).toFixed(3));
      }
    });

    

     if(!isLocateTab){
      result.push({
        secid:'Total Symbols',
        cusip: 'Total Cusip',
        total_request_count: "Total Req Count",
        total_quantity: "Total Req Qty",
        total_response_quantity: "Total Res Qty",
        totalFillQty: "Total Fill Qty",
        minrate: "Avg Min Rate",
        maxrate: "Avg Max Rate",
        avgrate: "Avg Rate",
        totalClientCount:'Max Client Count',
        isTotalRow: true,
        isTotalLabel: true,
        pnl:'Total PNL',
        total_fill_count:'Total Filled Count'
      });
     }
   

    rows["isTotalRow"] = true;
    rows["cusip"] = countUnique("cusip", api);
    rows["secid"] = countUnique("secid", api);
    rows["totalClientCount"] = getMaxValue('totalClientCount', api)
    result.push(rows);
    
    api?.setPinnedBottomRowData(result);
  };

  /**
   * Method counts the unique value for given attribute (for filtered values also)
   * @param attribute
   * @returns unique values count
   */
  const countUnique = (attribute: string, api: GridApi): any => {
  const uniqueItemsSet: Set<any> = new Set();
  api?.forEachNodeAfterFilter((rowNode: any) => {
    if (
      rowNode?.data instanceof Object &&
      attribute in rowNode.data &&
      rowNode.data[attribute] !== null &&
      rowNode.data[attribute] !== undefined &&
      String(rowNode.data[attribute]).trim() !== ""
    ) {
      uniqueItemsSet.add(rowNode.data[attribute]);
    }
  });
  return convertToCommas(uniqueItemsSet.size);
};

  useEffect(() => {
    if (isDeselectAll != undefined) gridRef.current?.api?.deselectAll();
  }, [isDeselectAll]);


  const autoGroupColumnDef = useMemo(() => {
    return {
      minWidth: 100,
      maxWidth: 200,
    };
  }, []);

  const getRowId = (params: any) => {
    if (params && params.data) {
      if (params.data.id) {
        return params.data.id;
      }
    }

    return makeid(5);
  };

  function makeid(length: any) {
    let result = "";
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }

    return result;
  }

  const sideBar = useMemo(() => {
    return {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: false,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: "",
    };
  }, []);

  function isForceRefreshSelected() {
    return (document.querySelector("#forceRefresh") as HTMLInputElement)
      ?.checked;
  }
  function isSuppressFlashSelected() {
    return (document.querySelector("#suppressFlash") as HTMLInputElement)
      ?.checked;
  }
  const onGridRowDataChanged = () => {
    const refreshParams: any = {
      force: isForceRefreshSelected(),
      suppressFlash: isSuppressFlashSelected(),
    };
    gridRef.current!.api.refreshCells(refreshParams);
  };

  const onFilterChanged = (params: FilterChangedEvent) => {
    const { api, columnApi } = params;
    getName(api, columnApi);
  };

  useEffect(() => {
    gridApi = gridRef.current!.api;
    var gridColumnApi = gridRef.current!.columnApi;
    getName(gridApi, gridColumnApi);
  }, [locateRows]);

  useEffect(() => {
    if(inputTicker && isLocateTab){
      const initRows = [...initialLocates]
      const rows = initRows?.filter((row: any)=> row?.secid === inputTicker)
      setLocateRows(rows)
    }else {
      if(isShowFilledLocates){
        const inits = [...initialLocates]
        const filteredRows = inits?.filter((r: any)=> r?.totalFillQty >0)
        setLocateRows(filteredRows)
      }else{
        setLocateRows([...initialLocates])
      }
      
    }
    
  }, [inputTicker, isShowFilledLocates]);

  useEffect(()=>{

    const initRows = [...initialLocates]
    setLocateRows(initRows)

  }, [isResetFilter])


  const onRowClicked = (params: any) => {
    if (params?.data?.secid) {
      onRowSelect(params?.data);
    }
  };


  return (
    <>
     <div className={"w-100 h-100" + gridTheme}>
       {title !== undefined && (
         <div className="title-subHeaderTitle"> {title} </div>
       )}
        {showScreenLoader && <SpinnerFullScreen></SpinnerFullScreen>}
        <AgGridReact
         ref={gridRef}
                 rowStyle={{
                   justifyContent: "center",
                   color: "#2D2D2D",
                   fontSize: "0.75rem",
                   fontWeight: "400",
                   fontStyle: "normal",
                 }}
                 rowHeight={22}
                 columnDefs={columnDefs}
                 getRowId={getRowId}
                 onRowDataUpdated={onGridRowDataChanged}
                 rowData={locateRows}
                 suppressRowClickSelection={!isLocateTab}
                 defaultColDef={defaultColDef}
                 suppressAggFuncInHeader={true}
                 enableCellChangeFlash={false}
                 enableRangeSelection={false}
                 onGridReady={onGridReady}
                 onFilterChanged={onFilterChanged}
                 rowSelection={"multiple"}
                 autoGroupColumnDef={autoGroupColumnDef}
                 groupSelectsChildren={true}
                 groupSelectsFiltered={true}
                 sideBar={sideBar}
                 pivotMode={false}
                 cacheQuickFilter={false}
                 getContextMenuItems={getContextMenuItems}
                 quickFilterText={globalSearch}
                 overlayLoadingTemplate={
                   '<span class="ag-overlay-loading-center">Please wait while your rows are loading</span>'
                 }
                 getRowStyle={getRowStyle}
                 onRowClicked={onRowClicked}
        ></AgGridReact>
      </div>
    </>
  );
};

export default MostLocatesGrid;
